// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { ConditionalTooltip } from '@/components/conditional-tooltip';
import { OverflowTypography } from '@/components/overflow-typography';
import { TablePaginationFooter } from '@/components/table-pagination-footer';
import {
  ArrowDropDownRounded,
  ChecklistRtlOutlined,
} from '@/constants/hardfin-standard-icons';
import { Sort } from '@/constants/sort';
import {
  FulfillmentBySkuReportFilter,
  FulfillmentBySkuReportRow,
  FulfillmentBySkuReportSortProperty,
} from '@/report/api/types';
import { FulfillmentBySkuReportStatusChip } from '@/report/components/fulfillment-by-sku-report-status-chip';
import { useFulfillmentBySkuReport } from '@/report/hooks/use-fulfillment-by-sku-report';
import { formatNumber } from '@/utilities/number';
import { pluralize } from '@/utilities/pluralize';
import {
  Button,
  ButtonGroup,
  Menu,
  MenuItem,
  Paper,
  Skeleton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Typography,
} from '@mui/material';
import { DateTime, Interval } from 'luxon';
import { MouseEvent, ReactNode, useState } from 'react';

interface Props {
  filter: FulfillmentBySkuReportFilter;
  sort: Sort<FulfillmentBySkuReportSortProperty>;
  page: number;
  pageSize: number;
  onPageChange: (page: number) => void;
  onSort: (property: FulfillmentBySkuReportSortProperty) => void;
  onRowClick: (row: FulfillmentBySkuReportRow) => void;
  onFulfill: (row: FulfillmentBySkuReportRow) => void;
  onFulfillProject: (row: FulfillmentBySkuReportRow) => void;
}

interface ViewProps {
  rows: FulfillmentBySkuReportRow[];
  totalCount: number;
  sort: Sort<FulfillmentBySkuReportSortProperty>;
  page: number;
  pageSize: number;
  onPageChange: (page: number) => void;
  onSort: (property: FulfillmentBySkuReportSortProperty) => void;
  onRowClick: (row: FulfillmentBySkuReportRow) => void;
  onFulfill: (row: FulfillmentBySkuReportRow) => void;
  onFulfillProject: (row: FulfillmentBySkuReportRow) => void;
}

/**
 * A line is fulfillable when it still has outstanding need. Fully fulfilled
 * lines (need 0) and overage lines (need < 0) have nothing left to order.
 */
function hasOutstandingNeed(row: FulfillmentBySkuReportRow): boolean {
  return row.netNeedQuantity > 0;
}

const FULLY_FULFILLED_TOOLTIP =
  'This line is fully fulfilled, so there is nothing left to order';
const OVERAGE_TOOLTIP =
  'This line is already over-fulfilled, so there is nothing left to order';

/**
 * Per-line fulfill controls: a primary action to fulfill just this line and a
 * secondary action to fulfill everything the line's project still needs. Both
 * stop row-click propagation so they don't also navigate into the project.
 *
 * Lines with no outstanding need keep the controls visible but disabled, with a
 * tooltip explaining why there is nothing left to order.
 */
function FulfillmentRowActions(props: {
  row: FulfillmentBySkuReportRow;
  onFulfill: (row: FulfillmentBySkuReportRow) => void;
  onFulfillProject: (row: FulfillmentBySkuReportRow) => void;
}) {
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);

  const isFulfillable = hasOutstandingNeed(props.row);
  const disabledTooltip = isFulfillable
    ? undefined
    : props.row.netNeedQuantity === 0
      ? FULLY_FULFILLED_TOOLTIP
      : OVERAGE_TOOLTIP;

  const handleOpenMenu = (event: MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    setMenuAnchor(event.currentTarget);
  };

  const handleCloseMenu = () => {
    setMenuAnchor(null);
  };

  return (
    <>
      <ConditionalTooltip arrow placement="left" tooltipText={disabledTooltip}>
        <ButtonGroup
          size="small"
          variant="outlined"
          disabled={!isFulfillable}
          aria-label="Fulfill actions"
        >
          <Button
            startIcon={<ChecklistRtlOutlined fontSize="small" />}
            onClick={(event) => {
              event.stopPropagation();
              props.onFulfill(props.row);
            }}
          >
            Fulfill
          </Button>
          <Button
            aria-label="More fulfill options"
            sx={{ px: 0.5 }}
            onClick={handleOpenMenu}
          >
            <ArrowDropDownRounded fontSize="small" />
          </Button>
        </ButtonGroup>
      </ConditionalTooltip>
      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={handleCloseMenu}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <MenuItem
          onClick={(event) => {
            // The menu renders in a portal, but React events still bubble
            // through the component tree to the row's click handler.
            event.stopPropagation();
            handleCloseMenu();
            props.onFulfillProject(props.row);
          }}
        >
          Fulfill all outstanding for project
        </MenuItem>
      </Menu>
    </>
  );
}

interface SortableHeaderProps {
  property: FulfillmentBySkuReportSortProperty;
  sort: Sort<FulfillmentBySkuReportSortProperty>;
  align?: 'left' | 'right';
  width?: string;
  minWidth?: number;
  maxWidth?: number;
  children: ReactNode;
  onSort: (property: FulfillmentBySkuReportSortProperty) => void;
}

const PROJECT_COLUMN_WIDTH = '20%';
const ITEM_COLUMN_WIDTH = '12%';
const DATE_COLUMN_WIDTH = '14%';
const QUANTITY_COLUMN_WIDTH = '7%';
const STATUS_COLUMN_WIDTH = '12%';
const PROJECT_COLUMN_MIN_WIDTH_PX = 140;
const PROJECT_COLUMN_MAX_WIDTH_PX = 260;
const ITEM_COLUMN_MIN_WIDTH_PX = 84;
const ITEM_COLUMN_MAX_WIDTH_PX = 156;
const DATE_COLUMN_MIN_WIDTH_PX = 132;
const DATE_COLUMN_MAX_WIDTH_PX = 200;
const QUANTITY_COLUMN_MIN_WIDTH_PX = 72;
const STATUS_COLUMN_MIN_WIDTH_PX = 128;
const ACTION_COLUMN_MIN_WIDTH_PX = 132;

export function FulfillmentBySkuReportTable(props: Props) {
  const { rows, totalCount } = useFulfillmentBySkuReport({
    filter: props.filter,
  });

  return (
    <FulfillmentBySkuReportTableView
      rows={rows}
      totalCount={totalCount}
      sort={props.sort}
      page={props.page}
      pageSize={props.pageSize}
      onPageChange={props.onPageChange}
      onSort={props.onSort}
      onRowClick={props.onRowClick}
      onFulfill={props.onFulfill}
      onFulfillProject={props.onFulfillProject}
    />
  );
}

export function FulfillmentBySkuReportTableView(props: ViewProps) {
  if (props.rows.length === 0) {
    return (
      <Stack spacing={1}>
        <Paper
          variant="outlined"
          sx={{
            alignItems: 'center',
            display: 'flex',
            justifyContent: 'center',
            minHeight: 180,
            p: 4,
          }}
        >
          <Typography variant="body2" color="text.secondary">
            No fulfillment rows found
          </Typography>
        </Paper>
        <TablePaginationFooter
          pagination={{
            page: props.page,
            pageSize: props.pageSize,
            onPageChange: props.onPageChange,
          }}
          totalCount={props.totalCount}
          entityOptions={{ singular: 'line' }}
        />
      </Stack>
    );
  }

  return (
    <Stack minWidth={0} spacing={1}>
      <TableContainer
        component={Paper}
        variant="outlined"
        sx={{ maxWidth: '100%' }}
      >
        <Table size="small" sx={{ '& th, & td': { px: 1.5 } }}>
          <TableHead>
            <TableRow>
              <SortableHeader
                property="project"
                sort={props.sort}
                width={PROJECT_COLUMN_WIDTH}
                minWidth={PROJECT_COLUMN_MIN_WIDTH_PX}
                maxWidth={PROJECT_COLUMN_MAX_WIDTH_PX}
                onSort={props.onSort}
              >
                Project
              </SortableHeader>
              <SortableHeader
                property="sku"
                sort={props.sort}
                width={ITEM_COLUMN_WIDTH}
                minWidth={ITEM_COLUMN_MIN_WIDTH_PX}
                maxWidth={ITEM_COLUMN_MAX_WIDTH_PX}
                onSort={props.onSort}
              >
                Item
              </SortableHeader>
              <SortableHeader
                property="demandStart"
                sort={props.sort}
                width={DATE_COLUMN_WIDTH}
                minWidth={DATE_COLUMN_MIN_WIDTH_PX}
                maxWidth={DATE_COLUMN_MAX_WIDTH_PX}
                onSort={props.onSort}
              >
                Date range
              </SortableHeader>
              <SortableHeader
                property="projectRequested"
                sort={props.sort}
                align="right"
                width={QUANTITY_COLUMN_WIDTH}
                minWidth={QUANTITY_COLUMN_MIN_WIDTH_PX}
                onSort={props.onSort}
              >
                Project requested
              </SortableHeader>
              <SortableHeader
                property="foRequested"
                sort={props.sort}
                align="right"
                width={QUANTITY_COLUMN_WIDTH}
                minWidth={QUANTITY_COLUMN_MIN_WIDTH_PX}
                onSort={props.onSort}
              >
                FO requested
              </SortableHeader>
              <SortableHeader
                property="pending"
                sort={props.sort}
                align="right"
                width={QUANTITY_COLUMN_WIDTH}
                minWidth={QUANTITY_COLUMN_MIN_WIDTH_PX}
                onSort={props.onSort}
              >
                Pending
              </SortableHeader>
              <SortableHeader
                property="shipped"
                sort={props.sort}
                align="right"
                width={QUANTITY_COLUMN_WIDTH}
                minWidth={QUANTITY_COLUMN_MIN_WIDTH_PX}
                onSort={props.onSort}
              >
                Shipped
              </SortableHeader>
              <SortableHeader
                property="delivered"
                sort={props.sort}
                align="right"
                width={QUANTITY_COLUMN_WIDTH}
                minWidth={QUANTITY_COLUMN_MIN_WIDTH_PX}
                onSort={props.onSort}
              >
                Delivered
              </SortableHeader>
              <SortableHeader
                property="netNeedQuantity"
                sort={props.sort}
                align="right"
                width={QUANTITY_COLUMN_WIDTH}
                minWidth={QUANTITY_COLUMN_MIN_WIDTH_PX}
                onSort={props.onSort}
              >
                Need (overage)
              </SortableHeader>
              <SortableHeader
                property="status"
                sort={props.sort}
                width={STATUS_COLUMN_WIDTH}
                minWidth={STATUS_COLUMN_MIN_WIDTH_PX}
                onSort={props.onSort}
              >
                Status
              </SortableHeader>
              <TableCell
                align="right"
                aria-label="Actions"
                sx={{ minWidth: ACTION_COLUMN_MIN_WIDTH_PX }}
              />
            </TableRow>
          </TableHead>
          <TableBody>
            {props.rows.map((row) => (
              <TableRow
                hover
                key={`${row.project.id}:${row.item.id}`}
                sx={{ cursor: 'pointer' }}
                onClick={() => props.onRowClick(row)}
              >
                <TableCell
                  width={PROJECT_COLUMN_WIDTH}
                  sx={{
                    maxWidth: PROJECT_COLUMN_MAX_WIDTH_PX,
                    minWidth: PROJECT_COLUMN_MIN_WIDTH_PX,
                  }}
                >
                  <Stack minWidth={0} spacing={0.5}>
                    <OverflowTypography
                      variant="body2"
                      fontWeight={600}
                      overflow="hidden"
                      textOverflow="ellipsis"
                      whiteSpace="nowrap"
                    >
                      {row.project.name}
                    </OverflowTypography>
                    <OverflowTypography
                      variant="body2"
                      color="text.secondary"
                      overflow="hidden"
                      textOverflow="ellipsis"
                      whiteSpace="nowrap"
                    >
                      {row.company?.name ?? 'Not set'}
                    </OverflowTypography>
                  </Stack>
                </TableCell>
                <TableCell
                  width={ITEM_COLUMN_WIDTH}
                  sx={{
                    maxWidth: ITEM_COLUMN_MAX_WIDTH_PX,
                    minWidth: ITEM_COLUMN_MIN_WIDTH_PX,
                  }}
                >
                  <Stack minWidth={0} spacing={0.5}>
                    <OverflowTypography
                      variant="body2"
                      overflow="hidden"
                      textOverflow="ellipsis"
                      whiteSpace="nowrap"
                    >
                      {row.item.sku}
                    </OverflowTypography>
                    <OverflowTypography
                      variant="body2"
                      color="text.secondary"
                      overflow="hidden"
                      textOverflow="ellipsis"
                      whiteSpace="nowrap"
                    >
                      {row.item.name}
                    </OverflowTypography>
                  </Stack>
                </TableCell>
                <TableCell
                  width={DATE_COLUMN_WIDTH}
                  sx={{
                    maxWidth: DATE_COLUMN_MAX_WIDTH_PX,
                    minWidth: DATE_COLUMN_MIN_WIDTH_PX,
                  }}
                >
                  <DemandDateRangeCell
                    start={row.demandStart}
                    end={row.demandEnd}
                  />
                </TableCell>
                <TableCell
                  align="right"
                  width={QUANTITY_COLUMN_WIDTH}
                  sx={{ minWidth: QUANTITY_COLUMN_MIN_WIDTH_PX }}
                >
                  {formatNumber(row.projectRequested)}
                </TableCell>
                <TableCell
                  align="right"
                  width={QUANTITY_COLUMN_WIDTH}
                  sx={{ minWidth: QUANTITY_COLUMN_MIN_WIDTH_PX }}
                >
                  {formatNumber(row.foRequested)}
                </TableCell>
                <TableCell
                  align="right"
                  width={QUANTITY_COLUMN_WIDTH}
                  sx={{ minWidth: QUANTITY_COLUMN_MIN_WIDTH_PX }}
                >
                  {formatNumber(row.pending)}
                </TableCell>
                <TableCell
                  align="right"
                  width={QUANTITY_COLUMN_WIDTH}
                  sx={{ minWidth: QUANTITY_COLUMN_MIN_WIDTH_PX }}
                >
                  {formatNumber(row.shipped)}
                </TableCell>
                <TableCell
                  align="right"
                  width={QUANTITY_COLUMN_WIDTH}
                  sx={{ minWidth: QUANTITY_COLUMN_MIN_WIDTH_PX }}
                >
                  {formatNumber(row.delivered)}
                </TableCell>
                <TableCell
                  align="right"
                  width={QUANTITY_COLUMN_WIDTH}
                  sx={{ minWidth: QUANTITY_COLUMN_MIN_WIDTH_PX }}
                >
                  {formatNumber(row.netNeedQuantity, { accounting: true })}
                </TableCell>
                <TableCell
                  width={STATUS_COLUMN_WIDTH}
                  sx={{
                    minWidth: STATUS_COLUMN_MIN_WIDTH_PX,
                    '& .MuiChip-root': { maxWidth: '100%' },
                    '& .MuiChip-label': {
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                    },
                  }}
                >
                  <FulfillmentBySkuReportStatusChip status={row.status} />
                </TableCell>
                <TableCell
                  align="right"
                  sx={{ minWidth: ACTION_COLUMN_MIN_WIDTH_PX }}
                >
                  <FulfillmentRowActions
                    row={row}
                    onFulfill={props.onFulfill}
                    onFulfillProject={props.onFulfillProject}
                  />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePaginationFooter
        pagination={{
          page: props.page,
          pageSize: props.pageSize,
          onPageChange: props.onPageChange,
        }}
        totalCount={props.totalCount}
        entityOptions={{ singular: 'line' }}
      />
    </Stack>
  );
}

export function FulfillmentBySkuReportTableSkeleton() {
  return (
    <Stack spacing={1}>
      <TableContainer component={Paper} variant="outlined">
        <Stack spacing={1} p={2}>
          <Skeleton variant="rectangular" height={32} />
          <Skeleton variant="rectangular" height={40} />
          <Skeleton variant="rectangular" height={40} />
          <Skeleton variant="rectangular" height={40} />
        </Stack>
      </TableContainer>
      <TablePaginationFooter
        pagination={{
          page: 1,
          pageSize: 25,
          onPageChange: () => undefined,
        }}
        totalCount={null}
        entityOptions={{ singular: 'line' }}
      />
    </Stack>
  );
}

function SortableHeader(props: SortableHeaderProps) {
  const isActive = props.sort.property === props.property;
  const direction = props.sort.direction;

  return (
    <TableCell
      align={props.align}
      width={props.width}
      sx={{
        maxWidth: props.maxWidth,
        minWidth: props.minWidth,
      }}
    >
      <TableSortLabel
        active={isActive}
        direction={isActive ? direction : 'asc'}
        onClick={() => props.onSort(props.property)}
      >
        {props.children}
      </TableSortLabel>
    </TableCell>
  );
}

function DemandDateRangeCell(props: {
  start: DateTime | null;
  end: DateTime | null;
}) {
  const relativeStartLabel = formatRelativeStartDate(props.start);

  return (
    <Stack minWidth={0} spacing={0.5}>
      <OverflowTypography
        variant="body2"
        color={
          props.start !== null || props.end !== null
            ? 'text.primary'
            : 'text.secondary'
        }
        overflow="hidden"
        textOverflow="ellipsis"
        whiteSpace="nowrap"
      >
        {formatDemandDateRange(props.start, props.end)}
      </OverflowTypography>
      {relativeStartLabel !== null && (
        <Typography variant="body2" color="text.secondary">
          {relativeStartLabel}
        </Typography>
      )}
    </Stack>
  );
}

function formatDemandDateRange(start: DateTime | null, end: DateTime | null) {
  if (start === null) {
    if (end === null) {
      return 'No dates set';
    }
    return `? – ${end.toLocaleString({ dateStyle: 'medium' })}`;
  }

  if (end === null) {
    return `${start.toLocaleString({ dateStyle: 'medium' })} – ?`;
  }

  if (start.hasSame(end, 'day')) {
    return start.toLocaleString({ dateStyle: 'medium' });
  }
  return Interval.fromDateTimes(start, end).toLocaleString({
    dateStyle: 'medium',
  });
}

function formatRelativeStartDate(start: DateTime | null) {
  if (start === null) {
    return null;
  }

  const today = DateTime.now().startOf('day');
  const startDay = start.startOf('day');
  const dayDifference = Math.trunc(startDay.diff(today, 'days').days);

  if (dayDifference === 0) {
    return 'Starts today';
  }
  if (dayDifference > 0) {
    return `Starts in ${pluralize(dayDifference, { singular: 'day' })}`;
  }
  return `Started ${pluralize(Math.abs(dayDifference), { singular: 'day' })} ago`;
}
